import { useI18n } from 'vue-i18n'
import type { DeviceCommand } from '@/api/types'
import { formatReportDate, tashkentDay } from '@/utils/datetime'

/**
 * Device events and commands in plain words, shared by the device history and the
 * notifications bell: an icon and tone, a title that says what happened, a detail
 * line, who did it, and day groups ("Bugun", "Kecha", a date) with friendly times.
 */

export type EventTone = 'success' | 'danger' | 'warn' | 'info' | 'neutral'

export interface EventText {
  id: string
  at: string
  icon: string
  tone: EventTone
  title: string
  detail: string | null
  by: string | null
}

/** What an event needs to be described: the device history's events and the bell's notifications both have it. */
export interface DescribableEvent {
  id: string
  eventType: string
  metadata: Record<string, unknown>
  createdAt: string
}

const COMMAND_ICONS: Record<string, string> = {
  LOCK: 'pi-lock', DEVICE_LOCK: 'pi-lock', ENABLE_LOST_MODE: 'pi-lock', UNLOCK: 'pi-lock-open', DISABLE_LOST_MODE: 'pi-lock-open',
  CLEAR_PASSCODE: 'pi-lock-open', PLAY_LOST_SOUND: 'pi-volume-up', PLAY_SOUND: 'pi-volume-up', STOP_SOUND: 'pi-volume-off',
  DEVICE_LOCATION: 'pi-map-marker', REQUEST_LOCATION: 'pi-map-marker', DEVICE_INFO: 'pi-info-circle', ERASE: 'pi-eraser',
  WIPE: 'pi-eraser', UNENROLL: 'pi-sign-out', INSTALL_RESTRICTIONS: 'pi-sliders-h', BLOCK_APPLE_ID: 'pi-user-minus',
  ALLOW_APPLE_ID: 'pi-user-plus', SECURITY_INFO: 'pi-shield', INSTALLED_APPS: 'pi-th-large', MANAGED_APPS: 'pi-th-large',
  INSTALL_APP: 'pi-download', REMOVE_APP: 'pi-trash', SET_WALLPAPER: 'pi-image',
}

const EVENT_LOOK: Record<string, [string, EventTone]> = {
  ENROLLMENT: ['pi-link', 'success'],
  UNENROLLED: ['pi-sign-out', 'neutral'],
  UNENROLLMENT: ['pi-sign-out', 'neutral'],
  LOCK: ['pi-lock', 'warn'],
  UNLOCK: ['pi-lock-open', 'success'],
  LOCATION_UPDATE: ['pi-map-marker', 'info'],
  SECURITY_ALERT: ['pi-exclamation-triangle', 'danger'],
  FACTORY_RESET: ['pi-eraser', 'danger'],
  ERROR: ['pi-times-circle', 'danger'],
  PAYMENT_STATUS_CHANGE: ['pi-wallet', 'info'],
  SETTINGS_UPDATE: ['pi-cog', 'neutral'],
  ADMIN_ACTION: ['pi-user', 'neutral'],
  POLICY_CHANGE: ['pi-send', 'info'],
  ORG_ACTIVATION_LOCK: ['pi-shield', 'info'],
  ORG_ACTIVATION_UNLOCK: ['pi-shield', 'success'],
}

const COMMAND_TONE: Record<string, EventTone> = {
  PENDING: 'neutral', SENT: 'info', ACKNOWLEDGED: 'success', EXECUTED: 'success', FAILED: 'danger', CANCELED: 'neutral',
}

/** Event types that say nothing by themselves: their action becomes the title. */
const GENERIC_EVENTS = new Set(['ADMIN_ACTION', 'POLICY_CHANGE', 'SETTINGS_UPDATE'])

const TIME = new Intl.DateTimeFormat('en-GB', { timeZone: 'Asia/Tashkent', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })

function text(meta: Record<string, unknown>, key: string): string | null {
  const value = meta[key]
  return typeof value === 'string' && value.trim() ? value : null
}

/** A person's name without the placeholder an empty last name leaves ("Admin -"). */
function person(name: string | null): string | null {
  const clean = name?.replace(/(\s+-)+\s*$/, '').trim()
  return clean || null
}

export function useEventText() {
  const { t, te } = useI18n()

  function label(prefix: string, code: string): string | null {
    const key = `${prefix}.${code}`
    return te(key) ? t(key) : null
  }

  function commandName(type: string): string {
    return label('commandType', type) ?? type
  }

  /** A text the server wrote; machine codes (UPPER_SNAKE) only when we have words for them. */
  function words(value: unknown, prefix: string): string | null {
    if (typeof value !== 'string' || !value.trim()) {
      return null
    }
    return /^[A-Z0-9_]+$/.test(value) ? label(prefix, value) : value
  }

  function describeEvent(e: DescribableEvent): EventText {
    const meta = e.metadata ?? {}
    const [icon, look] = EVENT_LOOK[e.eventType] ?? ['pi-circle', 'neutral' as EventTone]
    const by = person(text(meta, 'performedByName') ?? text(meta, 'initiatedBy') ?? text(meta, 'activatedBy') ?? text(meta, 'user'))
    const queuedType = e.eventType === 'POLICY_CHANGE' ? text(meta, 'type') : null
    if (queuedType) {
      const payload = (meta.payload ?? {}) as Record<string, unknown>
      const subject = text(payload, 'name') ?? text(payload, 'bundleId') ?? text(payload, 'identifier')
      return { id: e.id, at: e.createdAt, icon: COMMAND_ICONS[queuedType] ?? icon, tone: 'info', title: commandName(queuedType),
        detail: [subject, t('deviceHistory.queued')].filter(Boolean).join(' · '), by }
    }
    // A command canceled from the console: say which one, and what went with it.
    const canceledType = text(meta, 'commandType')
    if (canceledType) {
      const also = Number(meta.alsoCanceled ?? 0)
      const detail = [
        also > 0 ? t('deviceHistory.alsoCanceled', { n: also }) : null,
        meta.lostModeReleased === true ? t('deviceHistory.lostModeReleasedShort') : null,
      ].filter(Boolean).join(' · ')
      return { id: e.id, at: e.createdAt, icon: 'pi-ban', tone: 'neutral',
        title: t('deviceHistory.canceledTitle', { name: commandName(canceledType) }), detail: detail || null, by }
    }
    const status = text(meta, 'status')
    const failed = status === 'FAILED' || meta.ok === false
    const action = words(meta.action, 'deviceHistory.action') ?? words(meta.event, 'deviceHistory.action')
    // A generic event type ("Administrator action") is titled by what was actually done.
    const actionIsTitle = !!action && GENERIC_EVENTS.has(e.eventType)
    const detail = [
      actionIsTitle ? null : action,
      e.eventType.startsWith('ORG_ACTIVATION') ? words(status, 'deviceHistory.status') : null,
      text(meta, 'error'),
      words(meta.reason, 'deviceHistory.reason'),
      text(meta, 'address'),
    ].filter((part, i, all): part is string => !!part && all.indexOf(part) === i)
    return {
      id: e.id,
      at: e.createdAt,
      icon,
      tone: failed ? 'danger' : look,
      title: actionIsTitle ? action! : label('deviceEvent', e.eventType) ?? e.eventType,
      detail: detail.join(' · ') || null,
      by,
    }
  }

  function describeCommand(c: DeviceCommand): EventText {
    return {
      id: c.id,
      at: c.createdAt,
      icon: COMMAND_ICONS[c.type] ?? 'pi-send',
      tone: COMMAND_TONE[c.status] ?? 'neutral',
      title: commandName(c.type),
      detail: [c.subject, c.errorMessage ?? label('deviceHistory.commandState', c.status)].filter(Boolean).join(' · ') || null,
      by: null,
    }
  }

  /** Consecutive items of one Tashkent day, labeled "Bugun", "Kecha" or the date. */
  function groupByDay<T extends { at: string }>(items: T[]): { day: string; label: string; items: T[] }[] {
    const today = tashkentDay(new Date())
    const yesterday = tashkentDay(new Date(Date.now() - 86_400_000))
    const result: { day: string; label: string; items: T[] }[] = []
    for (const item of items) {
      const day = tashkentDay(item.at)
      let group = result[result.length - 1]
      if (!group || group.day !== day) {
        group = {
          day,
          label: day === today ? t('deviceHistory.today') : day === yesterday ? t('deviceHistory.yesterday') : formatReportDate(day),
          items: [],
        }
        result.push(group)
      }
      group.items.push(item)
    }
    return result
  }

  /**
   * "Hozirgina" / "5 daqiqa oldin" within the last hour, else the clock time. Our own words: browsers have no
   * Uzbek for Intl.RelativeTimeFormat and fall back to English.
   */
  function when(iso: string): string {
    const minutes = Math.floor((Date.now() - Date.parse(iso)) / 60_000)
    if (minutes < 1) {
      return t('deviceHistory.justNow')
    }
    if (minutes < 60) {
      return t('deviceHistory.minutesAgo', { n: minutes })
    }
    return TIME.format(new Date(iso))
  }

  return { describeEvent, describeCommand, groupByDay, when, commandName }
}
